import { useTranslation } from 'react-i18next'
import { ChangeEvent, useCallback } from 'react'

import ControlsBar from 'app/common/ui-next/controls-bar'
import PlainButton from 'app/common/ui-next/plain-button'
import { AcquiringItem, RefillTarget, RefillType } from 'app/dataTypes/acquiring/settings'
import { useCurrencySign } from 'app/common/components-next/currency'
import { PlainInputFocused } from 'app/common/ui/PlainInput'
import PlainInputIcon from 'app/common/ui/PlainInputIcon'
import PlainHelperText from 'app/common/ui-next/plain-helper-text'
import { toFixedValueCurrency } from 'app/common/lib/toFixedValue'

import { Header } from '../FundColumns'
import useStep2Header from './useStep2Header'

import styles from './Step2.module.scss';

type Props = {
  refillTarget: RefillTarget | null
  refillType: RefillType | null
  acquiringItem: AcquiringItem | null
  amount: string
  changeAmount: (value: string) => void
  onPrev: () => void
  onNext: () => void
}

const Step2 = ({
  refillTarget,
  refillType,
  acquiringItem,
  amount,
  changeAmount,
  onPrev,
  onNext,
}: Props) => {
  const { t } = useTranslation('finances')
  const { id, maxSum, minSum, isParentAndAccountOwner } = acquiringItem || {}
  const amountFloat = parseFloat(amount)

  const currency = useCurrencySign()

  const handleChangeAmount = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.currentTarget
      changeAmount(value)
    },
    [changeAmount],
  )

  const restrictAmount = typeof maxSum === 'number' ? maxSum : null
  const maxValueString = toFixedValueCurrency(restrictAmount, false, currency)

  const error = (() => {
    if (amount) {
      if (typeof minSum !== 'number' && amountFloat < 1) {
        // Число, не менее {{minSum}}, пожалуйста
        return t('amount_error', { minSum: toFixedValueCurrency(1, false, currency) })
      }
      if (typeof minSum === 'number' && amountFloat < minSum) {
        // Число, не менее {{minSum}}, пожалуйста
        return t('amount_error', { minSum: toFixedValueCurrency(minSum, false, currency) })
      }
      if (restrictAmount && amountFloat > restrictAmount) {
        return t('maximum_refill', { value: maxValueString })
      }
    }
    return ''
  })()

  const handleSubmit = useCallback(() => {
    onNext()
  }, [onNext])

  const header = useStep2Header({ refillTarget, refillType, targetID: id })
  return (
    <>
      <Header>{header}</Header>

      {restrictAmount === 0 && !isParentAndAccountOwner && (
        <div className={styles.alreadyFilled}>
          {t('maximum_refill_already', 'Счет уже пополнен на максимальную сумму')}
        </div>
      )}

      {restrictAmount === 0 && isParentAndAccountOwner && (
        <div className={styles.alreadyFilled}>
          {t('maximum_refill_already_all', 'Невозможно пополнить')}
        </div>
      )}

      {restrictAmount !== 0 && (
        <form onSubmit={handleSubmit}>
          <div className={styles.amount}>
            <PlainInputFocused
              label={t('step2_title', 'Введите сумму платежа') as string}
              placeholder={t('ammount_placeholder', 'Сумма платежа') as string}
              component={PlainInputIcon}
              icon={<span className={styles.inputCurrency}>{currency}</span>}
              value={amount}
              onChange={handleChangeAmount}
              isError={!!error}
              errorText={error}
            />
            {!!restrictAmount && (
              <PlainHelperText>
                {/* Максимум {{value}} */}
                {t('maximum_refill', { value: maxValueString })}
              </PlainHelperText>
            )}
          </div>

          <ControlsBar className={styles.buttons}>
            <PlainButton outline onClick={onPrev}>
              {t('back_bt', 'Назад')}
            </PlainButton>
            <PlainButton
              onClick={handleSubmit}
              type="submit"
              disabled={!!error || Number.isNaN(amountFloat)}
            >
              {t('pay_bt', 'Оплатить')}
            </PlainButton>
          </ControlsBar>
        </form>
      )}
    </>
  )
}

export default Step2
